import { type ReactNode } from "react";
import { Lock, CheckCircle2, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type TileStatus = "not_started" | "locked" | "in_progress" | "complete";

interface DashboardTileProps {
  title: string;
  description: string;
  status: TileStatus;
  icon: LucideIcon;
  onNavigate?: () => void;
  onLockedClick?: () => void;
  children?: ReactNode;
  /** Outcome tiles never show a Get Started button */
  isOutcome?: boolean;
  /** Custom background class for outcome tiles */
  outcomeBg?: string;
}

export function DashboardTile({
  title,
  description,
  status,
  icon: Icon,
  onNavigate,
  onLockedClick,
  children,
  isOutcome = false,
  outcomeBg,
}: DashboardTileProps) {
  /* ── complete ───────────────────────────── */
  if (status === "complete") {
    return (
      <div
        onClick={onNavigate}
        className={cn(
          "relative flex min-h-[220px] cursor-pointer flex-col rounded-xl border-t-[3px] border-t-[#0d9488] border-x border-b bg-card p-6 shadow-subtle transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
        )}
      >
        <CheckCircle2 className="absolute right-4 top-4 h-[18px] w-[18px] text-[#0d9488]" />
        <h3 className="text-base font-bold text-foreground">{title}</h3>
        <div className="mt-3 flex-1 text-sm text-muted-foreground">
          {children ?? (
            <span className="italic text-muted-foreground/60">Data loading…</span>
          )}
        </div>
      </div>
    );
  }

  /* ── in_progress ────────────────────────── */
  if (status === "in_progress") {
    return (
      <div
        className={cn(
          "flex min-h-[220px] flex-col rounded-xl border-l-[3px] border-l-primary border-y border-r bg-card p-6 shadow-subtle transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
        )}
      >
        <h3 className="text-base font-bold text-foreground">{title}</h3>
        <div className="mt-2 flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
          </span>
          <span className="text-xs font-medium text-primary">In progress</span>
        </div>
        <div className="flex-1" />
        <Button
          onClick={onNavigate}
          className="mt-4 w-full gap-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Continue →
        </Button>
      </div>
    );
  }

  /* ── locked ─────────────────────────────── */
  if (status === "locked") {
    return (
      <div
        onClick={onLockedClick}
        className={cn(
          "relative flex min-h-[220px] cursor-pointer flex-col items-center justify-center rounded-xl border bg-card p-6 opacity-70 shadow-subtle transition-all duration-200"
        )}
      >
        <Lock className="absolute right-4 top-4 h-4 w-4 text-muted-foreground" />
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <h3 className="mt-4 text-center text-base font-bold text-foreground">{title}</h3>
        <p className="mt-1.5 line-clamp-2 text-center text-[13px] text-muted-foreground">
          {description}
        </p>
        <div className="flex-1" />
        <Button
          disabled
          variant="outline"
          className="mt-4 w-full rounded-lg text-muted-foreground"
        >
          Locked
        </Button>
      </div>
    );
  }

  /* ── not_started (default) ──────────────── */
  if (isOutcome) {
    return (
      <div
        onClick={onNavigate}
        className={cn(
          "flex min-h-[220px] flex-col items-center rounded-xl border p-6 shadow-subtle transition-all duration-200 hover:shadow-md hover:-translate-y-0.5",
          outcomeBg ?? "bg-muted",
          onNavigate && "cursor-pointer"
        )}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <h3 className="mt-4 text-center text-base font-bold text-foreground">{title}</h3>
        <p className="mt-1.5 line-clamp-2 text-center text-[13px] text-muted-foreground">
          {description}
        </p>
        <div className="mt-3 flex-1">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onNavigate}
      className={cn(
        "flex min-h-[220px] cursor-pointer flex-col items-center rounded-xl border bg-card p-6 shadow-subtle transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h3 className="mt-4 text-center text-base font-bold text-foreground">{title}</h3>
      <p className="mt-1.5 line-clamp-2 text-center text-[13px] text-muted-foreground">
        {description}
      </p>
      <div className="flex-1" />
      <Button
        onClick={(e) => { e.stopPropagation(); onNavigate?.(); }}
        variant="outline"
        className="mt-4 w-full rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary"
      >
        Get Started
      </Button>
    </div>
  );
}
